// ParentSmartScheduler.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import API from './api';

/** ---------- Helpers: vreme ---------- */
function normalizeTime(val) {
  if (!val) return '';
  let t = String(val);
  if (t.includes('AM') || t.includes('PM')) {
    const [hm, part] = t.split(' ');
    let [h, m] = hm.split(':').map(Number);
    if (part === 'PM' && h < 12) h += 12;
    if (part === 'AM' && h === 12) h = 0;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }
  return t.slice(0, 5);
}

function toMinutes(t) {
  if (!t) return 0;
  const [h, m] = normalizeTime(t).split(':').map(Number);
  return h * 60 + m;
}

function minutesToHHMM(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}

function simplifyIntervals(list = []) {
  return (list || [])
    .map((x) => ({
      from: normalizeTime(x.from_time ?? x.from),
      to: normalizeTime(x.to_time ?? x.to),
    }))
    .filter((i) => toMinutes(i.to) > toMinutes(i.from));
}

function subtractIntervals(base = [], blocked = []) {
  let result = simplifyIntervals(base);
  const bad = simplifyIntervals(blocked);

  for (const exc of bad) {
    const excFrom = toMinutes(exc.from);
    const excTo = toMinutes(exc.to);
    const next = [];

    for (const it of result) {
      const a = toMinutes(it.from);
      const b = toMinutes(it.to);
      if (excTo <= a || excFrom >= b) {
        next.push(it);
      } else {
        if (excFrom > a) next.push({ from: it.from, to: normalizeTime(exc.from) });
        if (excTo < b) next.push({ from: normalizeTime(exc.to), to: it.to });
      }
    }

    result = next;
  }

  return result;
}

function mergeTouching(intervals = []) {
  const sorted = intervals.slice().sort((x, y) => toMinutes(x.from) - toMinutes(y.from));
  const out = [];
  for (const it of sorted) {
    if (!out.length) {
      out.push({ ...it });
    } else {
      const last = out[out.length - 1];
      if (toMinutes(it.from) <= toMinutes(last.to)) {
        if (toMinutes(it.to) > toMinutes(last.to)) last.to = it.to;
      } else {
        out.push({ ...it });
      }
    }
  }
  return out;
}

function clampTo(i, startM, endM) {
  const f = Math.max(toMinutes(i.from), startM);
  const t = Math.min(toMinutes(i.to), endM);
  if (t <= f) return null;
  return { from: minutesToHHMM(f), to: minutesToHHMM(t) };
}

/** ---------- Discipline / badge meta ---------- */
const DISCIPLINE_LABELS = {
  PROG_EI: {
    short: 'EI',
    label: 'Early Intervention',
    badgeClass: 'bg-blue-100 text-blue-800 border-blue-200',
  },
  PROG_PI: {
    short: 'PI',
    label: 'Project Impact',
    badgeClass: 'bg-orange-100 text-orange-800 border-orange-200',
  },
  PROG_ABA: {
    short: 'ABA',
    label: 'ABA therapy',
    badgeClass: 'bg-purple-100 text-purple-800 border-purple-200',
  },
  PROG_OT: {
    short: 'OT',
    label: 'Occupational therapy',
    badgeClass: 'bg-teal-100 text-teal-800 border-teal-200',
  },
  PROG_PT: {
    short: 'PT',
    label: 'Physical therapy',
    badgeClass: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  },
  PROG_ST: {
    short: 'ST',
    label: 'Speech therapy',
    badgeClass: 'bg-green-100 text-green-800 border-green-200',
  },
};

function getDisciplineShort(code) {
  return DISCIPLINE_LABELS[code]?.short || code || 'Discipline';
}

/** ---------- Badges ---------- */
function StatusBadge({ status }) {
  const map = {
    draft: 'bg-blue-100 text-blue-700',
    pending: 'bg-blue-100 text-blue-700',
    accepted: 'bg-green-200 text-green-900',
    complete: 'bg-green-200 text-green-900',
    canceled: 'bg-purple-200 text-purple-700',
  };
  const cls = map[status] || 'bg-gray-200 text-gray-700';
  return (
    <span className={`px-2 py-0.5 rounded text-xs capitalize ${cls}`}>
      {status || '—'}
    </span>
  );
}

function ReassignedBadge({ reassigned }) {
  const map = {
    pending: 'bg-blue-100 text-blue-700',
    accepted: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
  };
  if (!reassigned || !map[reassigned] || reassigned === 'none') return null;
  return (
    <span className={`px-2 py-0.5 rounded text-xs ${map[reassigned]}`}>
      Substitute: {reassigned}
    </span>
  );
}

function DisciplineBadge({ program_code, type }) {
  if (String(type).toLowerCase() === 'event') {
    return (
      <span className="px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-800 border border-yellow-300">
        Event
      </span>
    );
  }

  if (!program_code) return null;
  const meta = DISCIPLINE_LABELS[program_code];
  if (!meta) {
    return (
      <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700 border border-gray-200">
        {program_code}
      </span>
    );
  }
  return (
    <span className={`px-2 py-0.5 rounded text-xs border ${meta.badgeClass}`}>
      {meta.short}
    </span>
  );
}

/** ---------- Telehealth helpers (kao u admin smart) ---------- */
function getVisitMode(s = {}) {
  const raw =
    s.visit_mode ||
    s.mode ||
    s.delivery_mode ||
    s.session_type ||
    s.type_mode ||
    null;

  if (!raw) return { label: null, isTelehealth: false };

  const v = String(raw).toLowerCase().trim();

  if (['telehealth', 'th', 'online', 'virtual'].includes(v)) {
    return { label: 'Telehealth', isTelehealth: true };
  }

  if (['phone', 'call', 'telephone'].includes(v)) {
    return { label: 'Phone call', isTelehealth: false };
  }

  // Za parent view nas najviše zanima da istaknemo telehealth;
  // ostalo možemo i da ne prikazujemo.
  return { label: null, isTelehealth: false };
}

function getJoinLink(s = {}) {
  return (
    s.telehealth_join_link ||
    s.telehealth_link ||
    s.join_url ||
    s.join_link ||
    s.meet_url ||
    s.zoom_link ||
    null
  );
}

/** ---------- Toast ---------- */
function Toast({ text, onClose }) {
  if (!text) return null;
  return (
    <div className="fixed top-4 right-4 z-[9999] text-white px-3 py-2 rounded shadow bg-slate-700">
      <div className="flex items-center gap-3">
        <span className="text-sm">{text}</span>
        <button
          onClick={onClose}
          className="text-white/90 hover:text-white text-lg leading-none"
        >
          ×
        </button>
      </div>
    </div>
  );
}

/** ---------- Main: ParentSmartScheduler ---------- */
export default function ParentSmartScheduler() {
  const [children, setChildren] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [weekOptions, setWeekOptions] = useState([]);
  const [selectedWeekStart, setSelectedWeekStart] = useState('');
  const [weekDates, setWeekDates] = useState([]);

  const [studentAvailByDate, setStudentAvailByDate] = useState({});
  const [slots, setSlots] = useState({});
  const [serverDateStr, setServerDateStr] = useState('');

  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  // info modal
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoSlot, setInfoSlot] = useState(null);
  const [infoSlotDate, setInfoSlotDate] = useState('');

  // dynamic workday
  const [workday, setWorkday] = useState({ start: '08:00', end: '18:00' });

  const trackRefs = useRef({});

  /** ---------- Load server time + children + weeks + workday ---------- */
  useEffect(() => {
    async function init() {
      try {
        const [timeRes, childrenRes, weeksRes, workdayRes] = await Promise.allSettled([
          API.get('/server-time'),
          API.get('/parent/children'),
          API.get('/weeks'),
          API.get('/workday-settings'),
        ]);

        if (timeRes.status === 'fulfilled') {
          const d = timeRes.value.data?.serverTime || '';
          setServerDateStr(d.slice(0, 10));
        }

        if (childrenRes.status === 'fulfilled') {
          const arr = childrenRes.value.data || [];
          setChildren(arr);
          if (arr.length > 0) {
            setSelectedStudentId(String(arr[0].id));
          }
        } else {
          setChildren([]);
        }

        if (weeksRes.status === 'fulfilled') {
          setWeekOptions(weeksRes.value.data || []);
        }

        if (workdayRes.status === 'fulfilled') {
          const wd = workdayRes.value.data || {};
          setWorkday({
            start: (wd.start || '08:00').slice(0, 5),
            end: (wd.end || '18:00').slice(0, 5),
          });
        }
      } catch (e) {
        setStatus('Failed to load initial data.');
      }
    }
    init();
  }, []);

  /** ---------- Derived day metrics ---------- */
  const DAY_START = workday.start;
  const DAY_END = workday.end;
  const dayStartM = toMinutes(DAY_START);
  const dayEndM = toMinutes(DAY_END);
  const spanM = Math.max(1, dayEndM - dayStartM);

  const pctBox = (i) => {
    const left = ((toMinutes(i.from) - dayStartM) / spanM) * 100;
    const width = ((toMinutes(i.to) - toMinutes(i.from)) / spanM) * 100;
    return {
      left: `${Math.max(0, left)}%`,
      width: `${Math.max(0, Math.min(100 - left, width))}%`,
    };
  };

  /** ---------- Toast auto-clear ---------- */
  useEffect(() => {
    if (!status) return;
    const t = setTimeout(() => setStatus(''), 3000);
    return () => clearTimeout(t);
  }, [status]);

  const currentStudent = useMemo(
    () => children.find((c) => String(c.id) === String(selectedStudentId)) || null,
    [children, selectedStudentId]
  );

  /** ---------- Load week dates ---------- */
  useEffect(() => {
    if (!selectedWeekStart) {
      setWeekDates([]);
      return;
    }
    API.get(`/week-dates/${selectedWeekStart}`)
      .then((res) => setWeekDates(res.data || []))
      .catch(() => setWeekDates([]));
  }, [selectedWeekStart]);

  /** ---------- Load student availability ---------- */
  useEffect(() => {
    if (!selectedStudentId || !selectedWeekStart) {
      setStudentAvailByDate({});
      return;
    }

    API.get(
      `/student-availability/realweek/${selectedStudentId}/${selectedWeekStart}`
    )
      .then((res) => {
        const map = {};
        Object.entries(res.data || {}).forEach(([date, obj]) => {
          const intervals = simplifyIntervals(obj.intervals || []);
          const blocked = simplifyIntervals(obj.blocked || []);
          const free = subtractIntervals(intervals, blocked);
          map[date] = { free, intervals, blocked };
        });
        setStudentAvailByDate(map);
      })
      .catch(() => {
        setStudentAvailByDate({});
      });
  }, [selectedStudentId, selectedWeekStart]);

  /** ---------- Load existing visits for selected child/week ---------- */
  async function loadExistingVisits() {
    if (!selectedStudentId || !selectedWeekStart) {
      setSlots({});
      return;
    }

    setLoading(true);
    try {
      const res = await API.get(
        `/visits/week/${selectedStudentId}/${selectedWeekStart}`
      );
      const m = {};
      Object.entries(res.data || {}).forEach(([date, list]) => {
        m[date] = (list || []).map((s) => {
          const fromRaw =
            s.from || s.from_time || s.start || s.start_time || null;
          const toBaseRaw =
            s.to || s.to_time || s.end || s.end_time || null;
          const from = normalizeTime(fromRaw);

          const rawType = s.type || s.visit_type || '';
          const isEventSlot = String(rawType).toLowerCase() === 'event';

          const eventEndRaw =
            s.event_end_time || s.event_to || s.eventEnd || null;

          const endRaw = isEventSlot
            ? eventEndRaw || toBaseRaw || fromRaw
            : toBaseRaw || eventEndRaw || fromRaw;

          const to = normalizeTime(endRaw || fromRaw);

          const teacherName =
            s.teacher?.name ||
            s.teacher_name ||
            s.provider_name ||
            s.name ||
            null;

          // disciplina/program
          const rawProg =
            s.program_code ||
            s.discipline_code ||
            s.discipline_program_code ||
            s.discipline_prog ||
            s.service_type ||
            s.discipline_service_type ||
            null;

          let programCode = null;
          if (rawProg) {
            const up = String(rawProg).toUpperCase();
            if (up.startsWith('PROG_')) {
              programCode = up;
            } else if (['EI', 'PI', 'ABA', 'OT', 'PT', 'ST'].includes(up)) {
              programCode = `PROG_${up}`;
            } else {
              programCode = rawProg;
            }
          }

          const effectiveType = isEventSlot
            ? 'event'
            : s.type || s.visit_type || 'standard';

          return {
            ...s,
            from,
            to,
            teacher: teacherName ? { id: s.teacher_id, name: teacherName } : null,
            type: effectiveType,
            program_code: programCode,
          };
        });
      });
      setSlots(m);
    } catch (e) {
      setStatus('Failed to load visits.');
      setSlots({});
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadExistingVisits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudentId, selectedWeekStart]);

  /** ---------- Existing intervals per date (for layout) ---------- */
  const existingIntervalsByDate = useMemo(() => {
    const out = {};
    for (const { date } of weekDates) {
      const daySlots = slots[date] || [];
      const arr = daySlots.map((s) => ({ from: s.from, to: s.to }));
      out[date] = mergeTouching(arr);
    }
    return out;
  }, [slots, weekDates]);

  function isPast(date) {
    if (!serverDateStr) return false;
    return date < serverDateStr;
  }

  /** ---------- Info modal for parent ---------- */
  function openInfoModal(date, s) {
    setInfoSlot({ ...s });
    setInfoSlotDate(date);
    setShowInfoModal(true);
  }

  /** ---------- UI ---------- */
  return (
    <div className="p-2 max-w-7xl mx-auto">
      <Toast text={status} onClose={() => setStatus('')} />

      <h2 className="text-lg font-bold mb-1">
        Family schedule (read-only)
      </h2>
      <p className="text-xs text-slate-600 mb-4 max-w-2xl">
        View visits scheduled for your child or children. To request changes,
        please contact your agency directly.
      </p>

      {/* Filters row */}
      <div className="flex flex-col lg:flex-row gap-4 items-center lg:items-end mb-4">
        <div>
          <label className="block mb-1 text-sm font-medium">Child:</label>
          {children.length === 0 ? (
            <div className="text-sm text-gray-500">
              No children are linked to this parent account.
            </div>
          ) : children.length === 1 ? (
            <div className="border px-3 py-2 rounded bg-gray-50 font-semibold text-sm">
              {(currentStudent?.last_name || '') +
                ', ' +
                (currentStudent?.first_name || '')}
            </div>
          ) : (
            <select
              className="border px-2 py-1 w-64 text-sm rounded"
              value={selectedStudentId}
              onChange={(e) => setSelectedStudentId(e.target.value)}
            >
              {children
                .slice()
                .sort(
                  (a, b) =>
                    (a.last_name || '').localeCompare(b.last_name || '') ||
                    (a.first_name || '').localeCompare(b.first_name || '')
                )
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {(c.last_name || '') + ', ' + (c.first_name || '')}
                  </option>
                ))}
            </select>
          )}
        </div>

        {!!selectedStudentId && (
          <div>
            <label className="block mb-1 text-sm font-medium">Week:</label>
            <select
              className="border px-2 py-1 w-56 text-sm rounded"
              value={selectedWeekStart}
              onChange={(e) => setSelectedWeekStart(e.target.value)}
            >
              <option value="">-- Choose week --</option>
              {weekOptions.map((w, i) => (
                <option key={i} value={w.value}>
                  {w.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Legend */}
      {selectedStudentId && (
        <div className="flex flex-wrap items-center gap-4 text-xs mb-3">
          <span className="flex items-center gap-2">
            <span className="inline-block w-5 h-3 rounded bg-green-300" />{' '}
            Student availability
          </span>
          <span className="flex items-center gap-2">
            <span className="inline-block w-5 h-3 rounded bg-gray-300 border" />{' '}
            Scheduled visits
          </span>
        </div>
      )}

      {/* Timeline days */}
      {selectedWeekStart && weekDates.length > 0 && (
        <div className="space-y-4">
          {weekDates.map(({ date, dayName }) => {
            const sFree = (studentAvailByDate[date]?.free || [])
              .map((i) => clampTo(i, dayStartM, dayEndM))
              .filter(Boolean);

            const daySlots = slots[date] || [];
            const isPastDay = isPast(date);

            const savedCount = daySlots.length;

            return (
              <div key={date} className="border rounded p-3 shadow-sm">
                <div
                  className={`font-semibold mb-2 px-2 py-1 rounded flex items-center gap-2 ${
                    isPastDay
                      ? 'text-gray-400 bg-gray-50'
                      : 'text-gray-800 bg-gray-100'
                  }`}
                >
                  <span>
                    {dayName} – {date}
                  </span>
                  {savedCount > 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200">
                      {savedCount} visit{savedCount > 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                <div
                  className="relative h-16 rounded bg-gray-100 border select-none overflow-visible"
                  ref={(el) => (trackRefs.current[date] = el)}
                  data-date={date}
                >
                  {/* hour ticks */}
                  {Array.from({ length: spanM / 60 + 1 }).map((_, i) => {
                    const leftPct = (i * 60) / spanM * 100;
                    return (
                      <div key={i}>
                        <div
                          className="absolute top-4 bottom-0 border-l border-gray-300"
                          style={{ left: `${leftPct}%` }}
                        />
                        <div
                          className="absolute top-0 text-[10px] text-gray-500 -translate-x-1/2"
                          style={{ left: `${leftPct}%` }}
                        >
                          {minutesToHHMM(dayStartM + i * 60).slice(0, 2)}
                        </div>
                      </div>
                    );
                  })}

                  {/* student free */}
                  {sFree.map((seg, idx) => {
                    const box = pctBox(seg);
                    return (
                      <div
                        key={'s' + idx}
                        className="absolute top-4 bottom-1 bg-green-300 opacity-30 rounded"
                        style={{ left: box.left, width: box.width }}
                        title={`Student availability ${seg.from}–${seg.to}`}
                      />
                    );
                  })}

                  {/* visits (clickable for info modal) */}
                  {daySlots.map((s, idx) => {
                    const box = pctBox(s);
                    const isSaved = !!s.visit_id || s.status;
                    const baseBorder = isSaved
                      ? 'border-emerald-700'
                      : 'border-gray-400';

                    return (
                      <div
                        key={'c' + idx}
                        className={`absolute top-5 bottom-2 opacity-90 rounded border ${baseBorder} shadow-sm overflow-hidden bg-white cursor-pointer`}
                        style={{ left: box.left, width: box.width }}
                        onClick={() => openInfoModal(date, s)}
                        title="Click to see visit details"
                      >
                        <div className="absolute inset-0 flex items-center justify-between px-2 text-xs">
                          <span className="font-mono">
                            {s.from}–{s.to}
                          </span>
                          <span className="font-semibold truncate ml-2">
                            {s.teacher?.name || '—'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Text list (ispod timeline-a) */}
                {daySlots.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {daySlots.map((s, idx) => {
                      const { label: modeLabel, isTelehealth } = getVisitMode(s);
                      const joinUrl = getJoinLink(s);

                      return (
                        <div
                          key={'l' + idx}
                          className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm gap-1"
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold">
                              {s.teacher?.name || '—'}
                            </span>
                            <span className="font-mono">
                              {s.from}–{s.to}
                            </span>
                            <DisciplineBadge
                              program_code={s.program_code}
                              type={s.type}
                            />
                            <StatusBadge status={s.status || 'draft'} />
                            <ReassignedBadge
                              reassigned={s.reassigned_status}
                            />

                            {/* Telehealth badge + Join link (novi deo) */}
                            {isTelehealth && (
                              <span className="px-2 py-0.5 rounded text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 flex items-center gap-1">
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-indigo-500" />
                                {modeLabel || 'Telehealth'}
                              </span>
                            )}
                            {isTelehealth && joinUrl && (
                              <a
                                href={joinUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-indigo-700 underline"
                              >
                                Join visit
                              </a>
                            )}
                          </div>
                          <button
                            type="button"
                            className="text-xs text-sky-700 underline"
                            onClick={() => openInfoModal(date, s)}
                          >
                            Details
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {selectedStudentId && selectedWeekStart && !loading && weekDates.length === 0 && (
        <div className="mt-4 text-sm text-gray-500">
          No data for the selected week.
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="mt-4 text-sm text-gray-500 animate-pulse">
          Loading visits…
        </div>
      )}

      {/* Info modal (read-only) */}
      {showInfoModal && infoSlot && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-sm w-full p-4 relative">
            <button
              className="absolute top-2 right-2 text-gray-500 text-xl"
              onClick={() => {
                setShowInfoModal(false);
                setInfoSlot(null);
                setInfoSlotDate('');
              }}
            >
              ×
            </button>
            <h3 className="text-lg font-semibold mb-2">Visit details</h3>
            <div className="text-sm space-y-1">
              <div>
                <b>Date:</b> {infoSlotDate}
              </div>
              <div>
                <b>Time:</b> {infoSlot.from}–{infoSlot.to}
              </div>
              <div>
                <b>Teacher:</b> {infoSlot.teacher?.name || '—'}
              </div>
              <div className="flex items-center gap-2">
                <b>Type:</b>
                <DisciplineBadge
                  program_code={infoSlot.program_code}
                  type={infoSlot.type}
                />
              </div>
              <div className="flex items-center gap-2">
                <b>Status:</b>
                <StatusBadge status={infoSlot.status || 'draft'} />
              </div>
              {infoSlot.reassigned_status &&
                infoSlot.reassigned_status !== 'none' && (
                  <div className="flex items-center gap-2">
                    <b>Substitute:</b>
                    <ReassignedBadge
                      reassigned={infoSlot.reassigned_status}
                    />
                  </div>
                )}
              {infoSlot.notes && (
                <div className="mt-2">
                  <b>Notes:</b>
                  <div className="border rounded px-2 py-1 bg-gray-50 text-xs">
                    {infoSlot.notes}
                  </div>
                </div>
              )}
              <div className="mt-3 text-xs text-gray-500">
                If you need to change or cancel this visit, please contact your
                agency directly.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


